import { ErrorHandler, Injectable } from '@angular/core';
import { environment } from '../../environments/environment';
import { SnakebarService } from '../services/snakebar-service';

@Injectable()
export class GlobalErrorHandler implements ErrorHandler {
  constructor(private snakebarService: SnakebarService) {}

  handleError(error: unknown): void {
    const resolvedError = this.resolveError(error);

    // Reload once when a lazy chunk is missing after a new deploy.
    if (this.handleChunkLoadError(resolvedError)) {
      return;
    }

    this.snakebarService.showErrorMessage(
      'Unexpected error occurred. Please try again.',
    );

    if (!environment.production) {
      console.error('Unhandled application error:', resolvedError);
    }
  }

  private handleChunkLoadError(error: unknown): boolean {
    const message = (error as Error)?.message ?? '';
    const isChunkError =
      /ChunkLoadError/.test((error as Error)?.name ?? '') ||
      /Loading chunk [\w-]+ failed/i.test(message) ||
      /Failed to fetch dynamically imported module/i.test(message);

    if (isChunkError && !sessionStorage.getItem('chunkReloaded')) {
      sessionStorage.setItem('chunkReloaded', '1');
      window.location.reload();
      return true;
    }

    if (!isChunkError) {
      sessionStorage.removeItem('chunkReloaded');
    }
    return false;
  }

  private resolveError(error: unknown): unknown {
    if (isUnhandledPromiseRejection(error)) {
      return error.rejection;
    }
    return error;
  }
}

function isUnhandledPromiseRejection(
  error: unknown,
): error is { rejection: unknown } {
  return (
    typeof error === 'object' &&
    error !== null &&
    'rejection' in error &&
    (error as { rejection: unknown }).rejection !== undefined
  );
}
